import { Separator as SeparatorPrimitive } from "@base-ui/react/separator";
import type React from "react";
import { cn } from "../../lib/utils";

type SeparatorProps = SeparatorPrimitive.Props & {
  /**
   * Optional centered label ("or", "and", …) rendered between two rules.
   * Horizontal orientation only.
   */
  label?: string;
  /**
   * Omit for the neutral hairline, a seam between regions. `strong` paints
   * `--border-strong`: a rule that must read at a glance, a boundary the eye
   * is meant to find. `accent` paints the rule and its label in the brand
   * hue: a marker the reader is meant to find in a long feed ("New" above the
   * first unread entry).
   */
  tone?: "strong" | "accent" | undefined;
  /**
   * Where the label sits on a labelled rule. `center` (default) splits the
   * rule in two ("or"); `end` runs one rule up to a trailing label, the
   * reading-order shape of a "new from here" marker.
   */
  labelAlign?: "center" | "end";
  /**
   * A vertical rule's height in px. Omit to stretch to the cross axis. For a
   * short rule between two words on one line — a title and its live status,
   * a tab strip and its rail control — where a full-height rule would read
   * as a wall.
   */
  length?: number;
};

export function Separator({
  className,
  orientation = "horizontal",
  label,
  tone,
  labelAlign = "center",
  length,
  style,
  ...props
}: SeparatorProps): React.ReactElement {
  const fill =
    tone === "accent" ? "bg-brand" : tone === "strong" ? "bg-border-strong" : "bg-border";
  if (label != null && orientation === "horizontal") {
    // ONE separator, named by its label: the primitive keeps the role and takes
    // every caller prop, and the two hairlines are decoration inside it. Two
    // nested primitives would announce two unnamed dividers plus loose text.
    const rule = (
      <div
        aria-hidden
        className={cn("h-px min-w-0 flex-1 shrink", fill)}
        data-slot="separator-rule"
      />
    );
    return (
      <SeparatorPrimitive
        aria-label={label}
        className={cn("flex w-full shrink-0 items-center gap-3", className)}
        data-slot="separator-label"
        orientation="horizontal"
        {...props}
      >
        {rule}
        <span
          className={cn(
            "text-xs font-medium",
            tone === "accent" ? "text-brand" : "text-muted-foreground",
          )}
        >
          {label}
        </span>
        {labelAlign === "center" && rule}
      </SeparatorPrimitive>
    );
  }
  return (
    <SeparatorPrimitive
      className={cn(
        // Both orientations default to filling their cross axis, but only
        // when the caller has not sized that axis: a variant-scoped default
        // outranks a caller's plain `w-7`/`h-5` in specificity, so each
        // default is guarded on no explicit class for its own axis.
        "shrink-0 data-[orientation=horizontal]:h-px data-[orientation=horizontal]:not-[[class^='w-']]:not-[[class*='_w-']]:w-full data-[orientation=vertical]:w-px data-[orientation=vertical]:not-[[class^='h-']]:not-[[class*='_h-']]:self-stretch",
        fill,
        className,
      )}
      data-slot="separator"
      orientation={orientation}
      // Inline, because the vertical form's own `self-stretch` is a
      // data-orientation variant that out-ranks any plain alignment class.
      style={length != null ? { alignSelf: "center", height: length, ...style } : style}
      {...props}
    />
  );
}
